
import React from 'react';
import { 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Star, 
  TrendingUp, 
  Sparkles,
  Hash
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Game {
  id: string;
  title: string;
  description?: string;
  thumbnail?: string;
  category: string;
  hidden: boolean;
  featured: boolean;
  trending: boolean;
  is_new: boolean;
  rating?: number;
  ratings_count?: number;
  real_play_count: number;
  display_play_count: number;
}

interface AdminGameCardProps {
  game: Game;
  isSelected: boolean;
  onSelect: (gameId: string) => void;
  onEdit: (game: Game) => void;
  onEditFakePlayCount: (game: Game) => void;
  onToggleVisibility: (gameId: string, hidden: boolean) => void;
  onDelete: (gameId: string) => void;
}

const AdminGameCard: React.FC<AdminGameCardProps> = ({
  game,
  isSelected,
  onSelect,
  onEdit,
  onEditFakePlayCount,
  onToggleVisibility,
  onDelete
}) => {
  return (
    <Card className="p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between gap-4">
        {/* Left side - Checkbox, Thumbnail, and Game Info */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onSelect(game.id)}
            className="flex-shrink-0"
          />
          
          {game.thumbnail && (
            <img
              src={game.thumbnail}
              alt={game.title}
              className="w-12 h-12 rounded object-cover border flex-shrink-0"
            />
          )}
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {game.title}
              </h3>
              <div className="flex gap-1 flex-wrap">
                {game.featured && (
                  <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">
                    <Star className="w-3 h-3 mr-1" />
                    Featured
                  </Badge>
                )}
                {game.trending && (
                  <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Trending
                  </Badge>
                )}
                {game.is_new && (
                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                    <Sparkles className="w-3 h-3 mr-1" />
                    New
                  </Badge>
                )}
                {game.hidden && (
                  <Badge variant="destructive" className="text-xs">
                    Hidden
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="capitalize">{game.category}</span>
              <span>Display: {game.display_play_count?.toLocaleString() || 0}</span>
              <span className="text-green-600">Real: {game.real_play_count?.toLocaleString() || 0}</span>
              <span>Rating: {(game.rating || 0).toFixed(1)} ({game.ratings_count?.toLocaleString() || 0})</span>
            </div>
          </div>
        </div>
        
        {/* Right side - Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEditFakePlayCount(game)}
            className="text-purple-600 hover:text-purple-700"
          >
            <Hash className="w-4 h-4 mr-1" />
            Plays
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onToggleVisibility(game.id, !game.hidden)}
            className={game.hidden ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
          >
            {game.hidden ? (
              <>
                <EyeOff className="w-4 h-4 mr-1" />
                Show
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-1" />
                Hide
              </>
            )}
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onEdit(game)}
            className="text-blue-600 hover:text-blue-700"
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
          
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Game</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{game.title}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(game.id)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </Card>
  );
};

export default AdminGameCard;
